import React, { useEffect, useRef } from "react";
import { Button, Col, Typography, useTheme } from "@commonlibs";
import clsx from "clsx";
import { config } from "common/config";
import { TokenAmountConverter } from "domain/wallet/controllers/utils/TokenAmountConverter";
import Form from "ui/common/components/input/Form/Form";
import TextField from "ui/common/components/input/TextField/TextField";
import { useTranslate } from "ui/locale";
import useNavigateWithSendState from "ui/transactions/hooks/useNavigateWithSendState";
import { TransactionsRoutes } from "ui/transactions/router/TransactionsRouter";
import { SendState, SendStateNames } from "ui/transactions/transactions.types";
import UserBalanceField from "ui/user/containers/UserBalanceField/UserBalanceField";
import UserAddressTextField from "ui/wallet/containers/UserAddressTextField/UserAddressTextField";
import useSendQueryParams from "../../hooks/useSendQueryParams";
import { useNavigate } from "react-router-dom";
import { MainRoutes } from "ui/common/router/MainRouter.types";
import { CancelButton } from "./SendDetailsForm.styles";

export interface SendDetailsFormProps {
    className?: string;
    style?: React.CSSProperties;
}

function SendDetailsForm({ className, ...rest }: SendDetailsFormProps) {
    const translate = useTranslate();
    const { spacing } = useTheme();
    const navigateWithState = useNavigateWithSendState(TransactionsRoutes.SEND_CONFIRMATION);
    const navigate = useNavigate();

    const queryParams = useSendQueryParams();

    const queryParamsRef = useRef(queryParams);

    useEffect(() => {
        queryParamsRef.current = queryParams;
    }, [queryParams]);

    //This function is a verbose way to test the form
    function handleOnSubmit(data: SendState) {
        const { paymentId, reference } = queryParamsRef.current;

        navigateWithState({
            ...data,
            paymentId,
            reference,
        });
    }

    function onCancel() {
        navigate(MainRoutes.MAIN);
    }

    return (
        <Form onSubmit={handleOnSubmit} css={{ flex: 1 }} className={clsx("send-details-form", className)} {...rest}>
            <Col justifyContent="space-between" flex={1} gap={spacing["6"]}>
                <Col flex={1} gap={spacing["4"]}>
                    <Typography variant="body3Medium" light>
                        {translate("sendDetailsText")}
                    </Typography>
                    <UserBalanceField
                        validators={{ gt: "0" }}
                        defaultValue={
                            queryParams.amount ? TokenAmountConverter.formatToken(queryParams.amount, config.organization.decimals) : ""
                        }
                        label={translate("quantity")}
                        name={SendStateNames.AMOUNT}
                        required
                    />
                    <UserAddressTextField
                        defaultValue={queryParams.destinationAddress}
                        label={translate("sendTo")}
                        name={SendStateNames.DESTINATION_ADDRESS}
                        placeholder={"usuario@nutresa.com"}
                        readonly={Boolean(queryParams.destinationAddress)}
                        required
                    />
                    <TextField
                        defaultValue={queryParams.concept}
                        label={translate("concept")}
                        name={SendStateNames.CONCEPT}
                        placeholder={`(${translate("optional")})`}
                        validators={{ maxChars: 500 }}
                    />
                </Col>
                <Button variant="primary" type="submit">
                    {translate("sendPoints")}
                </Button>
                <CancelButton variant="tertiary" onClick={onCancel}>
                    {translate("cancel")}
                </CancelButton>
            </Col>
        </Form>
    );
}

export default SendDetailsForm;
